/**
 * Crypt Skin - Base Component Factory
 *
 * Creates crypt-themed color-specific base components using the crypt-specific
 * factory (gradient-based colors with ornate gold/bronze frame). Does NOT use
 * createColoredBase from createBaseVariants — the crypt SVG structure is
 * different from basics.
 */

import React from 'react';
import type { ColoredBaseProps } from '../../constants/types';
import CryptBaseComponent from './CryptBaseComponent';
import { CRYPT_COLOR_SCHEMES, type CryptBaseColorScheme } from './constants/baseColorSchemes';
import { RedPawn, BluePawn, YellowPawn, GreenPawn } from '../../pawns/crypt/pawnFactory';
import type { PawnProps } from '../../constants/types';

function createCryptBase(
  color: string,
  colorScheme: CryptBaseColorScheme,
  PawnComponent: React.ComponentType<PawnProps>
) {
  const displayName = `Crypt${color.charAt(0).toUpperCase() + color.slice(1)}Base`;

  const Base = React.memo(function CryptBase(props: ColoredBaseProps) {
    return (
      <CryptBaseComponent
        {...props}
        colorScheme={colorScheme}
        PawnComponent={props.PawnComponent ?? PawnComponent}
      />
    );
  });

  Base.displayName = displayName;
  return Base;
}

export const RedBase = createCryptBase('red', CRYPT_COLOR_SCHEMES.red, RedPawn);
export const BlueBase = createCryptBase('blue', CRYPT_COLOR_SCHEMES.blue, BluePawn);
export const YellowBase = createCryptBase('yellow', CRYPT_COLOR_SCHEMES.yellow, YellowPawn);
export const GreenBase = createCryptBase('green', CRYPT_COLOR_SCHEMES.green, GreenPawn);

export type { ColoredBaseProps as BlueBaseProps };
export type { ColoredBaseProps as RedBaseProps };
export type { ColoredBaseProps as GreenBaseProps };
export type { ColoredBaseProps as YellowBaseProps };
